import { theme } from '@resistapp/client/components/shared/theme';
import { friendlyCopyNumber } from '@resistapp/common/friendly';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { useRef, type MouseEvent } from 'react';
import { chartLayoutValues } from './bar-box-map/utils';

export interface Curve {
  xs: number[];
  ys: number[];
  color: string;
  id: string;
  /** Optional x-axis position (in data units) at which a vertical marker line should be drawn for this curve. */
  markerX?: number;
  /** Whether this curve corresponds to a disqualified tech rep */
  isRejected?: boolean;
  /** Whether this curve has no Ct (not detected) */
  isNotDetected?: boolean;
}

export interface VerticalReferenceLine {
  id: string;
  x: number;
  color?: string;
  strokeWidth?: number;
  strokeOpacity?: number;
}

export interface VerticalReferenceBand {
  id: string;
  xMin: number;
  xMax: number;
  fill?: string;
  fillOpacity?: number;
}

type RepNumber = 1 | 2 | 3;
export type QaRepDecisions = Record<RepNumber, boolean | null>;

interface Props {
  curves: Curve[];
  width: number;
  height: number;
  showRejected?: boolean;
  qaRepDecisions?: QaRepDecisions | null;
  verticalReferenceLines?: VerticalReferenceLine[];
  verticalReferenceBands?: VerticalReferenceBand[];
}

interface YAxisRange {
  yChartMin: number;
  yChartMax: number;
}

export function SimpleLineChart({
  curves,
  width,
  height,
  showRejected = false,
  qaRepDecisions,
  verticalReferenceLines = [],
  verticalReferenceBands = [],
}: Props) {
  const margin = { top: 10, right: 20, bottom: 40, left: 50 };
  const hoverGuideLineRef = useRef<SVGLineElement | null>(null);

  const xValuesRaw = [
    ...curves.flatMap(c => c.xs),
    ...curves.flatMap(c => (c.markerX == null ? [] : [c.markerX])),
    ...verticalReferenceLines.map(line => line.x),
    ...verticalReferenceBands.flatMap(band => [band.xMin, band.xMax]),
  ];
  const yValuesRaw = curves.flatMap(c => c.ys);

  const xValues = xValuesRaw.filter(x => Number.isFinite(x));
  const yValues = yValuesRaw.filter(y => Number.isFinite(y));

  const xChartMin: number = xValues.length === 0 ? 0 : xValues.reduce((min, v) => (v < min ? v : min), xValues[0]);
  const xChartMax: number = xValues.length === 0 ? 1 : xValues.reduce((max, v) => (v > max ? v : max), xValues[0]);

  const { yChartMin, yChartMax } = calculateYAxisRange(yValues);

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const xScale = scaleLinear({ domain: [xChartMin, xChartMax], range: [0, innerWidth] });
  const yScale = scaleLinear({ domain: [yChartMin < 0 ? yChartMin : 0, yChartMax], range: [innerHeight, 0] });

  // Calculate major tick values (approximate, since visx calculates them internally)
  const numMajorTicks = 6;
  const majorTickValues = xScale.ticks(numMajorTicks);

  // Calculate all integer values in the range
  const minInt = Math.ceil(xChartMin);
  const maxInt = Math.floor(xChartMax);
  const allIntegers = Array.from({ length: maxInt - minInt + 1 }, (_, i) => minInt + i);

  // Filter out integers that are already major ticks (within small tolerance)
  const minorTickValues = allIntegers.filter(int => {
    return !majorTickValues.some(majorTick => Math.abs(int - majorTick) < 0.1);
  });

  const getQaDecisionForCurve = (curve: Curve): boolean | null => {
    if (!qaRepDecisions) return null;
    const match = curve.id.match(/-rep(\d+)\b/i);
    const rep = match ? Number.parseInt(match[1], 10) : NaN;
    if (rep === 1 || rep === 2 || rep === 3) return qaRepDecisions[rep];
    return null;
  };

  const getStrokeDasharray = (curve: Curve): string | undefined => {
    const qaDecision = getQaDecisionForCurve(curve);
    if (qaDecision != null) {
      if (!qaDecision) return '1,10';
      return undefined;
    }
    if (showRejected && curve.isRejected) return '4,4';
    if (curve.isNotDetected) return '1,4';
    return undefined;
  };

  const getStrokeWidth = (curve: Curve): number => {
    const qaDecision = getQaDecisionForCurve(curve);
    return qaDecision != null ? 3 : 1;
  };

  const getStrokeOpacity = (curve: Curve): number | undefined => {
    const qaDecision = getQaDecisionForCurve(curve);
    if (qaDecision == null) return undefined;
    return qaDecision ? 1 : 0.5;
  };

  const getMarkerStrokeWidth = (curve: Curve): number => {
    const qaDecision = getQaDecisionForCurve(curve);
    return qaDecision != null ? 2 : 1;
  };

  const getStrokeLinecap = (curve: Curve): 'round' | undefined => {
    const qaDecision = getQaDecisionForCurve(curve);
    return qaDecision != null ? 'round' : undefined;
  };

  // Avoid React state here because chip pages can render thousands of curves and mousemove fires continuously.
  const showHoverGuide = (event: MouseEvent<SVGRectElement>) => {
    const hoverGuideLine = hoverGuideLineRef.current;
    if (!hoverGuideLine) {
      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();
    const scaledX = bounds.width === 0 ? 0 : ((event.clientX - bounds.left) / bounds.width) * innerWidth;
    const x = Math.min(innerWidth, Math.max(0, scaledX));

    hoverGuideLine.setAttribute('x1', String(x));
    hoverGuideLine.setAttribute('x2', String(x));
    hoverGuideLine.setAttribute('visibility', 'visible');
  };

  const hideHoverGuide = () => {
    hoverGuideLineRef.current?.setAttribute('visibility', 'hidden');
  };

  return (
    <svg width={width} height={height}>
      <Group left={margin.left} top={margin.top}>
        {verticalReferenceBands.map(band => {
          if (!Number.isFinite(band.xMin) || !Number.isFinite(band.xMax)) return null;
          const xMin = xScale(Math.min(band.xMin, band.xMax));
          const xMax = xScale(Math.max(band.xMin, band.xMax));
          return (
            <rect
              key={band.id}
              x={xMin}
              y={0}
              width={xMax - xMin}
              height={innerHeight}
              fill={band.fill ?? (theme.colors.neutral500 as string)}
              fillOpacity={band.fillOpacity ?? 0.18}
              pointerEvents="none"
            />
          );
        })}

        {/* Plot border */}
        <rect
          x={0}
          y={0}
          width={innerWidth}
          height={innerHeight}
          rx={8}
          ry={8}
          fill="none"
          stroke={theme.colors.neutral400}
        />

        {/* Data lines */}
        {curves.map(curve => (
          <LinePath
            key={curve.id}
            data={curve.xs.map((x, i) => ({ x, y: curve.ys[i] }))}
            x={d => xScale(d.x)}
            y={d => yScale(d.y)}
            stroke={curve.color}
            strokeWidth={getStrokeWidth(curve)}
            strokeOpacity={getStrokeOpacity(curve)}
            strokeDasharray={getStrokeDasharray(curve)}
            strokeLinecap={getStrokeLinecap(curve)}
            fill="none"
          />
        ))}

        {/* Vertical marker lines (Ct/Tm) - shown only when there are 3 or fewer curves */}
        {curves.length <= 3 &&
          curves.map(curve => {
            if (curve.markerX === undefined || !Number.isFinite(curve.markerX)) return null;
            const xPos = xScale(curve.markerX);
            return (
              <line
                key={`marker-${curve.id}`}
                x1={xPos}
                x2={xPos}
                y1={0}
                y2={innerHeight}
                stroke={curve.color}
                strokeWidth={getMarkerStrokeWidth(curve)}
                strokeOpacity={getStrokeOpacity(curve)}
                strokeDasharray={getStrokeDasharray(curve)}
                strokeLinecap={getStrokeLinecap(curve)}
              />
            );
          })}

        {verticalReferenceLines.map(line => {
          if (!Number.isFinite(line.x)) return null;
          return (
            <line
              key={line.id}
              x1={xScale(line.x)}
              x2={xScale(line.x)}
              y1={0}
              y2={innerHeight}
              stroke={line.color ?? (theme.colors.neutral800 as string)}
              strokeWidth={line.strokeWidth ?? 3}
              strokeOpacity={line.strokeOpacity ?? 0.9}
            />
          );
        })}

        <line
          ref={hoverGuideLineRef}
          className="chip-curve-hover-guide"
          x1={0}
          x2={0}
          y1={0}
          y2={innerHeight}
          stroke={theme.colors.neutral700}
          strokeWidth={1}
          strokeOpacity={0.75}
          strokeDasharray="3,3"
          visibility="hidden"
          pointerEvents="none"
        />
        <rect
          className="chip-curve-hover-target"
          x={0}
          y={0}
          width={innerWidth}
          height={innerHeight}
          fill="transparent"
          pointerEvents="all"
          onMouseEnter={showHoverGuide}
          onMouseMove={showHoverGuide}
          onMouseLeave={hideHoverGuide}
          style={{ cursor: 'crosshair' }}
        />

        {/* Minor tick marks for integers */}
        {minorTickValues.map(tickValue => (
          <line
            key={`minor-tick-${tickValue}`}
            x1={xScale(tickValue)}
            x2={xScale(tickValue)}
            y1={innerHeight}
            y2={innerHeight + 2}
            stroke={theme.colors.neutral400}
            strokeWidth={1}
          />
        ))}

        {/* Axes */}
        <AxisLeft
          scale={yScale}
          left={-chartLayoutValues.yAxisMarginLeft}
          tickStroke={theme.colors.neutral600}
          stroke="transparent"
          tickLength={4}
          hideAxisLine
          numTicks={6}
          tickFormat={value => friendlyCopyNumber(Number(value))}
          tickLabelProps={tickValue => ({
            fontSize: 14,
            fill: theme.colors.neutral700 as string,
            textAnchor: 'end',
            x: -12,
            y: yScale(tickValue) + 4,
          })}
        />
        <AxisBottom
          scale={xScale}
          top={innerHeight}
          tickStroke={theme.colors.neutral400}
          stroke="transparent"
          tickLength={4}
          hideAxisLine
          numTicks={6}
          tickFormat={value => friendlyCopyNumber(Number(value))}
          tickLabelProps={{
            fontSize: 14,
            fill: theme.colors.neutral700 as string,
            textAnchor: 'middle',
            dy: 16,
          }}
        />
      </Group>
    </svg>
  );
}

function calculateYAxisRange(yValues: number[]): YAxisRange {
  if (yValues.length === 0) {
    return { yChartMin: 0, yChartMax: 1 };
  }

  const yMinOriginal = yValues.reduce((min, v) => (v < min ? v : min), yValues[0]);
  const yMaxOriginal = yValues.reduce((max, v) => (v > max ? v : max), yValues[0]);
  const dataRange = yMaxOriginal - yMinOriginal;

  const padding = dataRange === 0 ? (yMaxOriginal === 0 ? 1 : Math.abs(yMaxOriginal)) * 0.05 : dataRange * 0.05;

  const initialYMin = yMinOriginal - padding;
  const initialYMax = yMaxOriginal + padding;

  return {
    yChartMin: initialYMin === initialYMax ? initialYMin - 0.05 : initialYMin,
    yChartMax: initialYMin === initialYMax ? initialYMax + 0.05 : initialYMax,
  };
}
